import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { portalApi } from "../api/portalApi";
import { PortalLoginResponse, PortalMeResponse } from "../types";

const PORTAL_TOKEN_KEY = "portalToken";
const PORTAL_USER_KEY = "portalUser";

interface PortalUser extends Omit<PortalLoginResponse, "token"> {}

interface PortalAuthContextValue {
  isAuthenticated: boolean;
  user: PortalUser | null;
  me: PortalMeResponse | null;
  login: (email: string, password: string, referralCode?: string | null) => Promise<void>;
  logout: () => void;
  refreshMe: () => Promise<void>;
}

const PortalAuthContext = createContext<PortalAuthContextValue | undefined>(undefined);

function readStoredUser(): PortalUser | null {
  const raw = localStorage.getItem(PORTAL_USER_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as PortalUser;
  } catch {
    localStorage.removeItem(PORTAL_USER_KEY);
    return null;
  }
}

export const PortalAuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<PortalUser | null>(() => {
    const token = localStorage.getItem(PORTAL_TOKEN_KEY);
    const stored = readStoredUser();
    if (!token || !stored) {
      if (!token && stored) {
        localStorage.removeItem(PORTAL_USER_KEY);
      }
      if (token && !stored) {
        localStorage.removeItem(PORTAL_TOKEN_KEY);
      }
      return null;
    }
    return stored;
  });
  const [me, setMe] = useState<PortalMeResponse | null>(null);

  const login = useCallback(async (email: string, password: string, referralCode?: string | null) => {
    const response = await portalApi.login(email, password);
    const { token, ...rest } = response;
    localStorage.setItem(PORTAL_TOKEN_KEY, token);
    localStorage.setItem(PORTAL_USER_KEY, JSON.stringify(rest));
    setUser(rest);
    const code = referralCode?.trim();
    if (code) {
      try {
        await portalApi.trackReferral(code);
      } catch {
        // Attribution is best-effort; login still succeeds.
      }
    }
    const profile = await portalApi.me();
    setMe(profile);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(PORTAL_TOKEN_KEY);
    localStorage.removeItem(PORTAL_USER_KEY);
    setUser(null);
    setMe(null);
  }, []);

  const refreshMe = useCallback(async () => {
    if (!localStorage.getItem(PORTAL_TOKEN_KEY)) {
      setMe(null);
      return;
    }
    try {
      const profile = await portalApi.me();
      setMe(profile);
    } catch {
      setMe(null);
    }
  }, []);

  const value = useMemo<PortalAuthContextValue>(
    () => ({
      isAuthenticated: Boolean(user),
      user,
      me,
      login,
      logout,
      refreshMe,
    }),
    [user, me, login, logout, refreshMe]
  );

  return <PortalAuthContext.Provider value={value}>{children}</PortalAuthContext.Provider>;
};

export function usePortalAuth(): PortalAuthContextValue {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) {
    throw new Error("usePortalAuth must be used within PortalAuthProvider.");
  }
  return ctx;
}
